import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { cn } from '../utils.ts';

interface TextareaProps extends ComponentPropsWithoutRef<'textarea'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, id, rows = 4, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `textarea-${reactId}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label
          htmlFor={inputId}
          className="text-caption uppercase tracking-[0.08em] font-medium text-cool-gray"
        >
          {label}
        </label>
      ) : null}
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        className={cn(
          'block w-full rounded-input border bg-surface text-midnight placeholder:text-cool-gray',
          'px-4 py-3 resize-y',
          'transition-all duration-fast ease-clickt',
          'focus-visible:outline-none focus-visible:shadow-focus-ring focus-visible:border-electric',
          'disabled:bg-light-silver/60 disabled:text-cool-gray disabled:cursor-not-allowed',
          error
            ? 'border-danger focus-visible:border-danger focus-visible:shadow-[0_0_0_4px_rgba(255,77,90,0.22)]'
            : 'border-border-subtle',
          className,
        )}
        {...rest}
      />
      {hint && !error ? (
        <p id={hintId} className="text-[13px] text-cool-gray leading-snug">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-[13px] text-danger leading-snug">
          {error}
        </p>
      ) : null}
    </div>
  );
});
